import { CSSProperties, FC, useState } from 'react';
import { Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { OutlineDocumentDuplicateIcon, OutlinePencilAltIcon } from '@tempmee/react-icons';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { TextAreaField } from '~/components/shared/forms';
import { CreatePostingMethodCard } from './CreatePostingMethodCard';

type TCreatePostingMethod = 'copy-paste' | 'manual';
type TStep = 'method' | 'paste';

interface ICreatePostingMethodPopupProps {
  /** Dismiss the popup (close icon / backdrop). */
  onClose: () => void;
  /** Selected "Manually add" then Next — continue to the existing guided form. */
  onManualAdd: () => void;
  /**
   * Selected "Copy & Paste" then Add — parse the pasted description and create a
   * draft. Resolves once navigation to Confirm Job Details has been triggered;
   * rejects to re-enable the form.
   */
  onSubmitDescription: (description: string) => Promise<void>;
}

const METHOD_MODAL_STYLE: CSSProperties = { width: 560 };
const PASTE_MODAL_STYLE: CSSProperties = { width: 600, padding: 48 };
const ICON_SIZE = 28;

export const CreatePostingMethodPopup: FC<ICreatePostingMethodPopupProps> = ({
  onClose,
  onManualAdd,
  onSubmitDescription,
}) => {
  const [step, setStep] = useState<TStep>('method');
  const [method, setMethod] = useState<TCreatePostingMethod | null>(null);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNext = () => {
    if (method === 'manual') {
      onManualAdd();
      return;
    }
    if (method === 'copy-paste') {
      setStep('paste');
    }
  };

  const handleAdd = async () => {
    setIsSubmitting(true);
    try {
      await onSubmitDescription(description);
    } catch {
      setIsSubmitting(false);
    }
  };

  if (step === 'paste') {
    return (
      <ConfirmPopup
        title="Add description"
        showClosePopupIcon
        closePopup={onClose}
        dismissiblePopup={false}
        modalStyle={PASTE_MODAL_STYLE}
        rightButtonText={isSubmitting ? 'Creating Job Posting' : 'Add'}
        rightButtonAction={handleAdd}
        rightButtonDisabled={!description.trim() || isSubmitting}
        rightButtonLoading={isSubmitting}
        rightButtonDataCy="create-posting-add-description-button"
        closePopupDataCy="create-posting-paste-close"
      >
        <Column width="100%" align="start" gap={8} style={{ marginBottom: 32 }}>
          <Typography as="span" bold size="body_large" color={colors.neutral[600]}>
            Job Description
          </Typography>
          <TextAreaField
            data-cy="create-posting-job-description-textarea"
            height={280}
            style={{ width: '100%', backgroundColor: colors.neutral[50] }}
            placeholder="Paste your job description here"
            value={description}
            disabled={isSubmitting}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Column>
      </ConfirmPopup>
    );
  }

  return (
    <ConfirmPopup
      title="How do you want to create your posting?"
      showClosePopupIcon
      closePopup={onClose}
      dismissiblePopup={false}
      modalStyle={METHOD_MODAL_STYLE}
      rightButtonText="Next"
      rightButtonAction={handleNext}
      rightButtonDisabled={!method}
      rightButtonDataCy="create-posting-method-next-button"
      closePopupDataCy="create-posting-method-close"
    >
      <Row justify="center" wrap="wrap" style={{ gap: 16, marginBottom: 32 }}>
        <CreatePostingMethodCard
          dataCy="create-posting-method-copy-paste"
          icon={<OutlineDocumentDuplicateIcon size={ICON_SIZE} color={colors.secondary[500]} />}
          label="Copy & Paste"
          description="Paste in your description & we will pre-fill for you."
          isSelected={method === 'copy-paste'}
          onClick={() => setMethod(method === 'copy-paste' ? null : 'copy-paste')}
        />
        <CreatePostingMethodCard
          dataCy="create-posting-method-manual"
          icon={<OutlinePencilAltIcon size={ICON_SIZE} color={colors.secondary[500]} />}
          label="Manually add"
          description="Build your own with a guided form."
          isSelected={method === 'manual'}
          onClick={() => setMethod(method === 'manual' ? null : 'manual')}
        />
      </Row>
    </ConfirmPopup>
  );
};
